import { useSyncExternalStore } from "react";

type Listener = () => void;

export const createPlaygroundMessagesStoreHook = <TMessage extends object>(
  initialMessages: TMessage[] = []
) => {
  const map = new Map<string, TMessage[]>();
  const listeners = new Set<Listener>();

  let version = 0;

  const emit = () => {
    version += 1;
    listeners.forEach((listener) => listener());
  };

  const subscribe = (listener: Listener) => {
    listeners.add(listener);

    return () => {
      listeners.delete(listener);
    };
  };

  const getSnapshot = () => version;

  const getMessages = (key: string): TMessage[] =>
    map.get(key) ?? initialMessages;

  const setMessagesFactory =
    (key: string) =>
    (value: TMessage[] | ((prev: TMessage[]) => TMessage[])) => {
      const prev = getMessages(key);
      const next = typeof value === "function" ? value(prev) : value;

      map.set(key, next);
      emit();
    };

  const usePlaygroundMessagesStore = (key: string) => {
    useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

    return {
      messages: getMessages(key),
      setMessages: setMessagesFactory(key),
    };
  };

  return usePlaygroundMessagesStore;
};
